import axios from 'axios'
import axiosRetry from 'axios-retry'
import { getAccessToken } from '../services/auth.service'
import store from '../store'
import { enqueueSnackbar } from '../actions/notification.actions'
import authLogger from './authLogger'

const instance = axios.create({
  headers: { Pragma: 'no-cache' },
})

// retry
axiosRetry(instance, {
    retries: 3, 
    retryDelay: (retryCount) => Math.pow(2, retryCount) * 1000, // Exponential backoff (in ms)
    retryCondition: (error) => {
        // Retry only on network errors or 5xx (transient)
        return axiosRetry.isNetworkOrIdempotentRequestError(error)
    }
})

// Request Interceptor
const requestHandler = async request => {
  try {
    const token = await getAccessToken()
    request.headers.Authorization = token
    return Promise.resolve(request)
  } catch (error) {
    return Promise.reject(error)
  }
}

// Success Response Interceptor
const responseSuccessHandler = response => {
  return response
}

// Error Response Interceptor
const responseErrorHandler = async error => {

  // network error
  if (!error.response) {
    authLogger.error('Axios', 'Network error - no response', {
      url: error.config?.url,
      message: error.message,
    })
    store.dispatch(
      enqueueSnackbar({
        message: `Could not complete request to ${error.config.url}`,
        options: {
          variant: 'error',
        },
      })
    )
    return Promise.reject(error)
  } else if (error.response.status === 401) {
      authLogger.warn('Axios', '401 Unauthorized response', {
        url: error.config?.url,
        status: error.response.status,
      })
      store.dispatch(
        enqueueSnackbar({
          message: `Unauthorized request`,
          options: {
            variant: 'warning',
          },
        })
      )
      // FIX: Always reject 401 errors - previously this didn't return anything
      // which caused the promise to resolve with undefined
      return Promise.reject(error)
  }

  // For all other errors, reject the promise
  return Promise.reject(error)
}

instance.interceptors.request.use(request => requestHandler(request))

instance.interceptors.response.use(
  response => responseSuccessHandler(response),
  error => responseErrorHandler(error)
)

export default instance
